/**
 * Shared visual contract for the sidebar navigation tree.
 *
 * Every list-item surface in the rail — Ask Puntt projects / chats / shared
 * chats AND the Documents folder tree — renders from these tokens so the
 * hierarchy, active state, and typography stay identical across sections.
 * Reuse these instead of re-deriving the classes per call site.
 */

import cx from "classnames";
import { useRef, useState } from "react";

import { Tooltip, TooltipContent, TooltipTrigger } from "./SidebarTooltip";

/**
 * Near-black + regular-weight title for a list-item row. Brand purple is
 * reserved for the single active row and the primary "View all" actions, so
 * the eye scans the list instead of reading a wall of identical bold-blue
 * links.
 */
export const SIDEBAR_ITEM_TITLE_CLASS =
  "block w-full truncate font-normal text-puntt-neutral-gray-12";

/**
 * Right-edge gradient FADE applied to a row title so the text dissolves
 * smoothly into the trailing "+" / "-" action button instead of hard-cutting
 * at an ellipsis — the button then reads as an integrated part of the row.
 *
 * The mask is PURELY PRESENTATIONAL: the full text stays in the DOM (so screen
 * readers still announce it whole and the truncation tooltip can surface it),
 * the mask only hides pixels. `~1.75rem` of fade lines up with where the
 * absolutely-positioned action button (`right-3` + its own width) sits, so the
 * fade tapers exactly under the button. A short name never reaches the fade
 * zone (the title box is `w-full`, the text sits at the left), so short names
 * are not visibly clipped.
 *
 * Applied as an inline style (not a Tailwind class) so the gradient is
 * guaranteed-correct CSS in the browser without relying on JIT parsing of an
 * arbitrary `mask-image` value, and so jsdom can assert the contract via the
 * `style` attribute.
 */
export const SIDEBAR_ROW_FADE_STYLE = {
  maskImage:
    "linear-gradient(to right, black calc(100% - 1.75rem), transparent)",
  WebkitMaskImage:
    "linear-gradient(to right, black calc(100% - 1.75rem), transparent)",
} as const;

/**
 * A row title that (1) fades its right edge into the trailing action button
 * (see `SIDEBAR_ROW_FADE_STYLE`) and (2) reveals the FULL name in a Radix
 * tooltip when — and only when — the text is actually truncated (overflow
 * detected on hover). Mirrors the Documents folder-row tooltip pattern so
 * chats, projects and folders read identically.
 */
export function SidebarRowTitle({
  name,
  testId,
  faded = true,
  className,
}: {
  name: string;
  testId?: string;
  /** Apply the right-edge fade. Off for rows with no trailing action button. */
  faded?: boolean;
  className?: string;
}) {
  const nameRef = useRef<HTMLSpanElement>(null);
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <Tooltip open={showTooltip} delayDuration={200}>
      <TooltipTrigger asChild>
        <span
          ref={nameRef}
          className={cx(SIDEBAR_ITEM_TITLE_CLASS, className)}
          style={faded ? SIDEBAR_ROW_FADE_STYLE : undefined}
          data-testid={testId}
          onMouseEnter={() => {
            const el = nameRef.current;
            if (el) setShowTooltip(el.scrollWidth > el.clientWidth);
          }}
          onMouseLeave={() => setShowTooltip(false)}
        >
          {name}
        </span>
      </TooltipTrigger>
      <TooltipContent side="right" sideOffset={8} align="start">
        {name}
      </TooltipContent>
    </Tooltip>
  );
}

/** Secondary metadata line under a title (status / who-when). */
export const SIDEBAR_ITEM_META_CLASS =
  "flex w-full items-center gap-1.5 truncate text-puntt-neutral-gray-10";
export const SIDEBAR_ITEM_META_STYLE = { fontSize: "11px", lineHeight: "14px" };

/**
 * One clear active state: a faint purple wash that neutral-grey hover cannot
 * be mistaken for. Exactly one row carries it, paired with `<SidebarActiveBar/>`.
 */
export const SIDEBAR_ACTIVE_ROW_CLASS =
  "bg-puntt-accent-3 hover:bg-puntt-accent-3";

/**
 * Indentation staircase. Root nav items (Ask Puntt / Documents, which carry a
 * leading icon) sit at the base. Section group headers (Projects / Chats /
 * Shared / Folders) step in one unit, and the leaf rows beneath them step in a
 * second unit, so the rail reads as a hierarchy instead of a flat
 * left-aligned wall. Both keep `pr-2` for the trailing "+" / action affordance.
 */
export const SIDEBAR_SECTION_HEADER_INDENT = "pl-4";
export const SIDEBAR_ITEM_INDENT = "pl-7";

/** Purple left bar marking the single active row (no layout shift). */
export function SidebarActiveBar() {
  return (
    <span
      aria-hidden
      className="absolute inset-y-1 left-0 w-0.5 rounded-full bg-puntt-accent-9"
      data-testid="sidebar-ask-puntt-active-bar"
    />
  );
}
